import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { 
  FiDollarSign, 
  FiUsers, 
  FiAlertCircle, 
  FiCheckCircle,
  FiTrendingUp,
  FiCalendar,
  FiCreditCard,
  FiFileText
} from 'react-icons/fi';
import './DashboardBAAK.css';

const DashboardBAAK = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalPembayaran: 0,
    totalTagihan: 0,
    tagihanLunas: 0,
    tagihanBelumLunas: 0,
    tagihanTerlambat: 0,
    totalMahasiswa: 0,
    pembayaranBulanIni: 0,
    tunggakanTotal: 0
  });
  const [recentPayments, setRecentPayments] = useState([]);
  const [overduePayments, setOverduePayments] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [
        pembayaranRes,
        tagihanRes,
        mahasiswaRes
      ] = await Promise.all([
        axios.get('/api/finance/pembayaran-mahasiswa'),
        axios.get('/api/tagihan'),
        axios.get('/api/students', { params: { limit: 10000 } })
      ]);

      const pembayaran = pembayaranRes.data.data || [];
      const tagihan = tagihanRes.data.data || [];
      const mahasiswa = mahasiswaRes.data.data || [];

      // Calculate stats
      const totalPembayaran = pembayaran.reduce((sum, p) => sum + p.nominal_dibayar, 0);
      const tagihanLunas = tagihan.filter(t => t.status === 'lunas').length;
      const tagihanBelumLunas = tagihan.filter(t => t.status === 'belum_lunas').length;
      const tagihanTerlambat = tagihan.filter(t => 
        t.status === 'belum_lunas' && new Date(t.deadline) < new Date()
      ).length;

      // Pembayaran bulan ini
      const now = new Date();
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const pembayaranBulanIni = pembayaran
        .filter(p => new Date(p.tanggal_pembayaran) >= firstDayOfMonth)
        .reduce((sum, p) => sum + p.nominal_dibayar, 0);

      // Total tunggakan
      const tunggakanTotal = tagihan
        .filter(t => t.status === 'belum_lunas')
        .reduce((sum, t) => sum + t.jumlah, 0);

      // Recent payments (5 terakhir)
      const recent = pembayaran
        .sort((a, b) => new Date(b.tanggal_pembayaran) - new Date(a.tanggal_pembayaran))
        .slice(0, 5);

      // Overdue payments (tagihan terlambat)
      const overdue = tagihan
        .filter(t => t.status === 'belum_lunas' && new Date(t.deadline) < new Date())
        .sort((a, b) => new Date(a.deadline) - new Date(b.deadline))
        .slice(0, 5);

      // Monthly data (6 bulan terakhir)
      const monthly = [];
      for (let i = 5; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const monthName = date.toLocaleDateString('id-ID', { month: 'short', year: 'numeric' });
        const monthPayments = pembayaran.filter(p => {
          const payDate = new Date(p.tanggal_pembayaran);
          return payDate.getMonth() === date.getMonth() && 
                 payDate.getFullYear() === date.getFullYear();
        });
        const total = monthPayments.reduce((sum, p) => sum + p.nominal_dibayar, 0);
        monthly.push({ month: monthName, total });
      }

      setStats({
        totalPembayaran,
        totalTagihan: tagihan.length,
        tagihanLunas,
        tagihanBelumLunas,
        tagihanTerlambat,
        totalMahasiswa: mahasiswa.length,
        pembayaranBulanIni,
        tunggakanTotal
      });

      setRecentPayments(recent);
      setOverduePayments(overdue);
      setMonthlyData(monthly);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error(error.response?.data?.message || 'Gagal mengambil data dashboard');
    } finally {
      setLoading(false);
    }
  };

  const formatRupiah = (angka) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(angka);
  };

  const formatTanggal = (tanggal) => {
    return new Date(tanggal).toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return <div className="spinner"></div>;
  }

  return (
    <div className="dashboard-baak">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-content">
          <div className="header-text">
            <h1>Dashboard BAAK</h1>
            <p>Monitoring Keuangan & Pembayaran Mahasiswa</p>
          </div>
          <div className="header-date">
            <FiCalendar />
            <span>{new Date().toLocaleDateString('id-ID', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}</span>
          </div>
        </div>
      </div>

      {/* Main Stats Grid */}
      <div className="stats-grid-main">
        <div className="stat-card-large stat-primary">
          <div className="stat-icon-wrapper">
            <FiDollarSign className="stat-icon" />
          </div>
          <div className="stat-content">
            <h3>{formatRupiah(stats.totalPembayaran)}</h3>
            <p>Total Pembayaran Masuk</p>
            <div className="stat-footer">
              <span className="stat-badge badge-success">
                <FiTrendingUp /> All Time
              </span>
            </div>
          </div>
        </div>

        <div className="stat-card-large stat-success">
          <div className="stat-icon-wrapper">
            <FiCheckCircle className="stat-icon" />
          </div>
          <div className="stat-content">
            <h3>{formatRupiah(stats.pembayaranBulanIni)}</h3>
            <p>Pembayaran Bulan Ini</p>
            <div className="stat-footer">
              <span className="stat-badge badge-info">
                <FiCalendar /> {new Date().toLocaleDateString('id-ID', { month: 'long' })}
              </span>
            </div>
          </div>
        </div>

        <div className="stat-card-large stat-warning">
          <div className="stat-icon-wrapper">
            <FiAlertCircle className="stat-icon" />
          </div>
          <div className="stat-content">
            <h3>{formatRupiah(stats.tunggakanTotal)}</h3>
            <p>Total Tunggakan</p>
            <div className="stat-footer">
              <span className="stat-badge badge-warning">
                {stats.tagihanBelumLunas} Tagihan
              </span>
            </div>
          </div>
        </div>

        <div className="stat-card-large stat-info">
          <div className="stat-icon-wrapper">
            <FiUsers className="stat-icon" />
          </div>
          <div className="stat-content">
            <h3>{stats.totalMahasiswa}</h3>
            <p>Total Mahasiswa</p>
            <div className="stat-footer">
              <span className="stat-badge badge-primary">
                <FiFileText /> Aktif
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Secondary Stats */}
      <div className="stats-grid-secondary">
        <div className="stat-card-small">
          <div className="stat-icon-small stat-icon-success">
            <FiCheckCircle />
          </div>
          <div className="stat-details-small">
            <h4>{stats.tagihanLunas}</h4>
            <p>Tagihan Lunas</p>
          </div>
        </div>

        <div className="stat-card-small">
          <div className="stat-icon-small stat-icon-warning">
            <FiAlertCircle />
          </div>
          <div className="stat-details-small">
            <h4>{stats.tagihanBelumLunas}</h4>
            <p>Belum Lunas</p>
          </div>
        </div>

        <div className="stat-card-small">
          <div className="stat-icon-small stat-icon-danger">
            <FiAlertCircle />
          </div>
          <div className="stat-details-small">
            <h4>{stats.tagihanTerlambat}</h4>
            <p>Terlambat</p>
          </div>
        </div>

        <div className="stat-card-small">
          <div className="stat-icon-small stat-icon-info">
            <FiFileText />
          </div>
          <div className="stat-details-small">
            <h4>{stats.totalTagihan}</h4>
            <p>Total Tagihan</p>
          </div>
        </div>
      </div>

      {/* Charts and Tables Row */}
      <div className="content-grid">
        {/* Monthly Chart */}
        <div className="card chart-card">
          <div className="card-header">
            <h3><FiTrendingUp /> Trend Pembayaran 6 Bulan Terakhir</h3>
          </div>
          <div className="card-body">
            <div className="chart-container">
              {monthlyData.map((data, index) => {
                const maxValue = Math.max(...monthlyData.map(d => d.total));
                const height = maxValue > 0 ? (data.total / maxValue) * 100 : 0;
                return (
                  <div key={index} className="chart-bar-wrapper">
                    <div className="chart-bar-container">
                      <div 
                        className="chart-bar" 
                        style={{ height: `${height}%` }}
                        title={formatRupiah(data.total)}
                      >
                        <span className="chart-value">{formatRupiah(data.total)}</span>
                      </div>
                    </div>
                    <span className="chart-label">{data.month}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Recent Payments */}
        <div className="card table-card">
          <div className="card-header">
            <h3><FiCreditCard /> Pembayaran Terbaru</h3>
          </div>
          <div className="card-body">
            {recentPayments.length === 0 ? (
              <div className="empty-state">
                <FiCreditCard />
                <p>Belum ada pembayaran</p>
              </div>
            ) : (
              <div className="payments-list">
                {recentPayments.map((payment, index) => (
                  <div key={index} className="payment-item">
                    <div className="payment-info">
                      <div className="payment-name">
                        <strong>{payment.mahasiswa_id?.name || '-'}</strong>
                        <span className="payment-nim">{payment.mahasiswa_id?.nim || '-'}</span>
                      </div>
                      <div className="payment-details">
                        <span className="payment-type">{payment.jenis_biaya}</span>
                        <span className="payment-date">{formatTanggal(payment.tanggal_bayar)}</span>
                      </div>
                    </div>
                    <div className="payment-amount">
                      {formatRupiah(payment.nominal_dibayar)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Overdue Payments */}
      {overduePayments.length > 0 && (
        <div className="card alert-card">
          <div className="card-header card-header-danger">
            <h3><FiAlertCircle /> Tagihan Terlambat - Perlu Tindakan</h3>
          </div>
          <div className="card-body">
            <div className="overdue-list">
              {overduePayments.map((tagihan, index) => {
                const daysOverdue = Math.floor(
                  (new Date() - new Date(tagihan.deadline)) / (1000 * 60 * 60 * 24)
                );
                return (
                  <div key={index} className="overdue-item">
                    <div className="overdue-info">
                      <div className="overdue-student">
                        <strong>{tagihan.nim}</strong>
                        <span className="overdue-type">{tagihan.jenis_tagihan}</span>
                      </div>
                      <div className="overdue-details">
                        <span className="overdue-semester">{tagihan.semester} - {tagihan.tahun_ajaran}</span>
                        <span className="overdue-deadline">
                          Deadline: {formatTanggal(tagihan.deadline)}
                        </span>
                      </div>
                    </div>
                    <div className="overdue-right">
                      <div className="overdue-amount">{formatRupiah(tagihan.jumlah)}</div>
                      <span className="overdue-badge badge-danger">
                        {daysOverdue} hari terlambat
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="quick-actions-grid">
        <a href="/baak/pembayaran" className="action-card action-primary">
          <FiCreditCard className="action-icon" />
          <div className="action-content">
            <h4>Input Pembayaran</h4>
            <p>Catat pembayaran mahasiswa</p>
          </div>
        </a>

        <a href="/baak/tagihan" className="action-card action-warning">
          <FiFileText className="action-icon" />
          <div className="action-content">
            <h4>Kelola Tagihan</h4>
            <p>Manage tagihan mahasiswa</p>
          </div>
        </a>

        <a href="/baak/laporan" className="action-card action-success">
          <FiDollarSign className="action-icon" />
          <div className="action-content">
            <h4>Laporan Keuangan</h4>
            <p>Generate laporan</p>
          </div>
        </a>

        <a href="/baak/status-pembayaran" className="action-card action-info">
          <FiCheckCircle className="action-icon" />
          <div className="action-content">
            <h4>Cek Status</h4>
            <p>Status pembayaran mahasiswa</p>
          </div>
        </a>
      </div>
    </div>
  );
};

export default DashboardBAAK;
